import React, { useCallback, useState, useMemo } from 'react';
import { useTranslation } from '@shared/core';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { AccommodationCombined, AccommodationCombinedType, AccommodationSourceType } from '../Accommodations/Accommodation.types';
import { pxToRem } from '@withjoy/joykit/theme';
import { useMediaQuery } from '@withjoy/joykit/utils';
import { useLayout } from '@apps/guest/packages/layout-engine/layouts/LayoutProvider';
import { SkeletonText, SkeletonThumbnail } from '@shared/components/Skeleton';
import { TextEllipsisWrapper } from '@shared/utils/formatting/textEllipsis';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useTextLineClampOverflow } from '@shared/hooks/useTextLineClampOverflow';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import {
  hed2ToSubHeadingOverride,
  label3ToParagraphOverride,
  body1ToParagraphOverride,
} from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useScaleGuestSiteFontSize } from '@apps/guest/components/GuestSiteTypographyOverride/hooks/useScaleGuestSiteFontSize';
import { Category } from '@graphql/generated';
import { useAccommodationsCta } from '../../hooks/useAccommodationsCta';
import { DEFAULT_HOTEL_IMAGE, DEFAULT_HOTEL_IMAGE_WIDE } from '@shared/utils/constants';
import { JoyPlacePricing } from './components/JoyPlacePricing';
import { JoyPlacePricingV2 } from './components/JoyPlacePricingV2';
import { EventVenue } from '@shared/utils/eventPlace';
import { isInIframe } from '@shared/utils/isInIframe';
import { RoomBlockPricingNonGrid } from './components/RoomBlockPricingNonGrid';
import { animationTransition } from '@shared/utils/animationTransition';
import { CTAButton } from './components/CTAButton';
import { ReservationsCount } from './components/ReservationsCount';
import { VenueAndTravelDistance } from './components/VenueAndTravelDistance';
import { PhotoCarousel } from '@shared/components/PhotoCarousel';
import { wrapPartnerHotelRedirect } from '../WrapPartnerHotelRedirect';
import { AccommodationsExternalHotelLinkInterceptor } from './components/AccommodationsExternalHotelLinkInterceptor';
import { StarRating } from '@shared/components';
import { ACCOMMODATIONS_NOTE_TEXT_CLASS_NAME } from '@apps/guest/packages/layout-engine/layouts/layout.constants';
import {
  COMPARISON_PRICE_MIN_HEIGHT,
  PRICE_MIN_HEIGHT,
  HORIZONTAL_ASPECT_RATIO,
  VERTICAL_ASPECT_RATIO,
  HORIZONTAL_IMAGE_MAX_WIDTH,
  HORIZONTAL_IMAGE_MAX_HEIGHT,
  VERTICAL_IMAGE_MAX_HEIGHT,
  EVENT_PHOTO_SIZE,
} from './AccommodationsTileV2.constants';
import { useIsMobileOrTablet, useIsMobileScreen } from '@shared/utils/media/useMediaScreens';
import { useHotelDiscount } from '@shared/components/DiscountsProvider/hotels/HotelsDiscountsProvider';
import { DiscountCouponBadge } from '@apps/stays/components/DiscountCouponBadge';
import { createSearchParamsFromAccommodationsPage } from '@apps/guest/routes/HotelBooking/HotelBooking.routes';

interface AccommodationsTileV2Props
  extends Readonly<{
    idx: number;
    locationInfo?: EventVenue;
    showDefaultData?: boolean;
    accommodation: AccommodationCombined;
    reservationCount?: number;
    isInterceptEnabled: boolean;
    accommodationPropertyPhotos?: { url: string }[];
    setLocationBeingHovered?: (id: string | undefined) => void;
    onSelectAccommodation?: React.MouseEventHandler;
    from?: AccommodationSourceType;
    onAccommodationsConsoleOpen: () => void;
    isAccommodationLinkInterceptionEnabled: boolean;
    enableHoverShadow?: boolean;
    enableHorizontalView?: boolean;
    eventPhoto?: string | null | undefined;
  }> {}

const AccommodationsTileV2 = wrapPartnerHotelRedirect((props: AccommodationsTileV2Props) => {
  const {
    idx,
    locationInfo,
    accommodation,
    reservationCount,
    isInterceptEnabled,
    onSelectAccommodation,
    setLocationBeingHovered,
    accommodationPropertyPhotos,
    onAccommodationsConsoleOpen,
    isAccommodationLinkInterceptionEnabled,
    enableHoverShadow,
    enableHorizontalView,
    eventPhoto,
  } = props;
  const { displayName, description, photo, id, type, interestedGuestCount, distance, distances, starRating } = accommodation;

  const { layout } = useLayout();
  const inPreviewMode = isInIframe();
  const isMobile = useIsMobileScreen();
  const isMobileOrTablet = useIsMobileOrTablet();
  const joyHotelDiscount = useHotelDiscount();

  const [showInterceptModal, setShowInterceptModal] = useState(false);

  const isBrannan = layout === 'brannan';

  const { ctaTitle, ctaUrl, ctaVariant, isLinkEnabled, shouldShowButton } = useAccommodationsCta({
    accommodation,
    locationInfo,
    from: AccommodationSourceType.AccomPageGrid,
    searchParams: createSearchParamsFromAccommodationsPage('accommodations_tile'),
    onSoldOutItemClickAction: onAccommodationsConsoleOpen,
  });

  const [showPricingEnabled] = useIsFeatureEnabled('travelShowHotelPrice');

  const showRegularHotelPricing = useMemo(
    () => !accommodation.customUrl && accommodation.isFromAccommodationCatalog && showPricingEnabled && !inPreviewMode,
    [accommodation.customUrl, accommodation.isFromAccommodationCatalog, showPricingEnabled, inPreviewMode],
  );

  const [showNewPriceComparisonUI] = useIsFeatureEnabled('enableAccommodationsPriceComparisonV2');
  const [isAccommodationsExpandedMapEnabled] = useIsFeatureEnabled('accommodationsExpandedMap');
  const shouldEnableNewLayout = isAccommodationsExpandedMapEnabled && !isMobile && isBrannan;

  const hasPrice = useMemo(
    () => type === AccommodationCombinedType.Roomblock || (type === AccommodationCombinedType.Hotel && showRegularHotelPricing),
    [type, showRegularHotelPricing],
  );

  const handleOnAccommodationClick = useEventCallback(e => {
    if (
      isInterceptEnabled &&
      isAccommodationLinkInterceptionEnabled &&
      accommodation.joyPlaceId &&
      accommodation.isCustomLinkEnabled &&
      accommodation.type === AccommodationCombinedType.Custom
    ) {
      e.preventDefault();
      e.stopPropagation();

      setShowInterceptModal(true);
      return;
    } else {
      onSelectAccommodation?.(e);
    }
  });

  const handleInterceptClose = useEventCallback(() => {
    setShowInterceptModal(false);
  });

  const renderTagComponent = useCallback(() => {
    if (reservationCount) {
      return <ReservationsCount reservationsCount={reservationCount} />;
    } else if (interestedGuestCount && interestedGuestCount > 1) {
      return <InterestedGuestsLabel interestedGuestCount={interestedGuestCount} />;
    }
    return null;
  }, [interestedGuestCount, reservationCount]);
  const hoverCSS = useMemo(
    () => ({
      _hover: {
        cursor: shouldShowButton ? 'pointer' : 'default',
        transform: shouldShowButton && !isMobile ? `translateY(${pxToRem(-2)})` : 'none',
        ...(enableHoverShadow && {
          boxShadow: shouldShowButton && !isMobile ? '0px 8px 32px 0px rgba(0, 0, 0, 0.12), 0px 2px 8px 0px rgba(0, 0, 0, 0.08)' : '0px 4px 16px 0px rgba(0, 0, 0, 0.08)',
        }),
      },
    }),
    [shouldShowButton, isMobile, enableHoverShadow],
  );

  const handleMouseEnter = useCallback(() => {
    if (!isMobileOrTablet) {
      setLocationBeingHovered?.(id);
    }
  }, [isMobileOrTablet, setLocationBeingHovered, id]);

  const handleMouseLeave = useCallback(() => {
    if (!isMobileOrTablet) {
      setLocationBeingHovered?.(undefined);
    }
  }, [isMobileOrTablet, setLocationBeingHovered]);

  const linkProps = useMemo(() => (isLinkEnabled ? { as: 'a' as const, href: ctaUrl, target: '_blank' } : {}), [isLinkEnabled, ctaUrl]);

  const carouselImages = useMemo(
    () =>
      accommodationPropertyPhotos?.map(photo => ({ src: photo?.url, alt: displayName || '' })) ?? [
        { src: photo?.url || (isMobile ? DEFAULT_HOTEL_IMAGE : DEFAULT_HOTEL_IMAGE_WIDE), alt: displayName || '' },
      ],
    [accommodationPropertyPhotos, photo?.url, displayName, isMobile],
  );

  return (
    <>
      <Flex
        position="relative"
        flex={!isBrannan || isMobile ? 'auto' : `1 1 calc(30% - ${pxToRem(10)})`}
        className="group"
        onClick={handleOnAccommodationClick}
        {...linkProps}
        {...(shouldEnableNewLayout && { backgroundColor: 'white' })}
        __css={hoverCSS}
        backgroundColor="white"
        borderRadius={pxToRem(12)}
        boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.04)"
        flexDirection={enableHorizontalView ? 'row' : 'column'}
        justifyContent="space-between"
        transition={animationTransition('boxShadow', 'transform', {
          duration: '300ms',
        })}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        overflow="hidden"
      >
        <Box
          width="100%"
          position="relative"
          {...(enableHorizontalView && { maxWidth: pxToRem(HORIZONTAL_IMAGE_MAX_WIDTH) })}
        >
          <Flex
            gap={pxToRem(4)}
            top={pxToRem(4)}
            left={pxToRem(4)}
            right={pxToRem(4)}
            zIndex={1}
            flexWrap="wrap"
            position="absolute"
            justifyContent="space-between"
          >
            <Box
              width={pxToRem(32)}
              height={pxToRem(32)}
              columnGap={pxToRem(2)}
              display={'flex'}
              borderRadius="50%"
              alignItems="center"
              justifyContent="center"
              backdropFilter={`blur(${pxToRem(4)})`}
              backgroundColor={`rgba(247, 247, 247, 0.6)`}
            >
              <TextV2
                color="mono14"
                fontWeight={500}
                fontFamily="Inter UI"
                typographyVariant="label3"
              >
                {+idx + 1}
              </TextV2>
            </Box>
            {
              <Box
                display="flex"
                alignItems="center"
                justifyContent={'flex-end'}
                gap={1}
              >
                {renderTagComponent()}
                <DiscountCouponBadge
                  discount={joyHotelDiscount}
                  accommodationType={accommodation.type}
                />
              </Box>
            }
          </Flex>
          <Box
            width="100%"
            aspectRatio={enableHorizontalView ? HORIZONTAL_ASPECT_RATIO : VERTICAL_ASPECT_RATIO}
            overflow={'hidden'}
            position={'relative'}
            height={'100%'}
            maxHeight={enableHorizontalView ? pxToRem(HORIZONTAL_IMAGE_MAX_HEIGHT) : pxToRem(VERTICAL_IMAGE_MAX_HEIGHT)}
            backgroundColor={'mono2'}
          >
            <PhotoCarousel
              aspectRatio={enableHorizontalView ? HORIZONTAL_ASPECT_RATIO : VERTICAL_ASPECT_RATIO}
              images={carouselImages}
              navigation={{ variant: 'minimal', visible: 'default' }}
            />
          </Box>
        </Box>

        <Box
          width="100%"
          height="100%"
          display="flex"
          flexDirection="column"
          padding={enableHorizontalView ? pxToRem(24) : pxToRem(16.19)}
        >
          <Box
            display="flex"
            gap={pxToRem(4)}
            flexDirection="column"
          >
            {displayName ? (
              <Box>
                <VenueDisplayName displayName={displayName} />
              </Box>
            ) : null}

            <Flex
              alignItems="center"
              justifyContent="flex-start"
              gap={pxToRem(4)}
            >
              {!!starRating && (
                <StarRating
                  rating={starRating}
                  id={accommodation.id}
                  hideNonFilledStars
                />
              )}
              <VenueAndTravelDistance
                distances={distances}
                milesToVenue={distance}
                marginTop={pxToRem(0)}
                useShortWordingDistance={true}
              />
            </Flex>
          </Box>

          <Box
            flex={1}
            display="flex"
            gap={pxToRem(3)}
            marginTop={pxToRem(3)}
            flexDirection="column"
          >
            {description ? (
              <Flex
                gap={3}
                {...(enableHorizontalView && { marginY: 3 })}
              >
                {eventPhoto && enableHorizontalView && (
                  <Box
                    width={pxToRem(EVENT_PHOTO_SIZE)}
                    backgroundImage={`url(${eventPhoto})`}
                    height={pxToRem(EVENT_PHOTO_SIZE)}
                    borderRadius={'full'}
                    overflow="hidden"
                    backgroundSize="cover"
                    backgroundPosition="center"
                  />
                )}
                <Description description={description} />
              </Flex>
            ) : null}

            {hasPrice && (
              <Box
                width="100%"
                {...(!enableHorizontalView && { minHeight: pxToRem(showNewPriceComparisonUI ? COMPARISON_PRICE_MIN_HEIGHT : PRICE_MIN_HEIGHT) })}
              >
                {/* ROOM BLOCK */}
                {type === AccommodationCombinedType.Roomblock && (
                  <RoomBlockPricingNonGrid
                    accommodation={accommodation}
                    marginTop={pxToRem(4)}
                  />
                )}
                {/* HOTEL: only enabled by experiment under treatment condition */}
                {type === AccommodationCombinedType.Hotel && showRegularHotelPricing ? (
                  showNewPriceComparisonUI ? (
                    <JoyPlacePricingV2
                      accommodation={accommodation}
                      locationInfo={locationInfo}
                      showEventOnlyRateTag={!enableHorizontalView}
                      marginTop={pxToRem(4)}
                    />
                  ) : (
                    <JoyPlacePricing
                      accommodation={accommodation}
                      showEventOnlyRateTag={!enableHorizontalView}
                      marginTop={pxToRem(4)}
                      isLargeTile={!isBrannan && !isMobile}
                      isFromTiles={true}
                    />
                  )
                ) : null}
              </Box>
            )}
          </Box>

          {shouldShowButton && (
            <Box
              marginTop="auto"
              paddingTop={enableHorizontalView ? pxToRem(16) : pxToRem(32)}
            >
              <CTAButton
                width="100%"
                ctaTitle={ctaTitle}
                variant={ctaVariant}
              />
            </Box>
          )}
        </Box>
      </Flex>

      <AccommodationsExternalHotelLinkInterceptor
        isOpen={showInterceptModal}
        accommodation={accommodation}
        onClose={handleInterceptClose}
        onSelectAccommodation={onSelectAccommodation}
      />
    </>
  );
});

const AccommodationsTileV2Skeleton: React.FC = () => {
  const { layout } = useLayout();
  const isBrannan = layout === 'brannan';
  const isMobile = useMediaQuery(theme => theme.mediaQueries.between(0, { breakpointAlias: 'sm2' }));
  const isGridView = isBrannan && !isMobile;

  return (
    <Flex
      flex={isBrannan ? `1 1 calc(33.33% - ${pxToRem(10)})` : `1 1 calc(30% - ${pxToRem(10)})`}
      borderRadius={pxToRem(12)}
      overflow="hidden"
      backgroundColor="white"
      boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.06), 0px 1px 3px 0px rgba(0, 0, 0, 0.04)"
      flexDirection="column"
      justifyContent="space-between"
    >
      {/* Image - no padding, extends to edges */}
      <SkeletonThumbnail
        height={pxToRem(206)}
        overrides={{
          PlaceholderRoot: {
            props: {
              width: '100%',
              backgroundColor: 'white',
            },
          },
        }}
      />

      <Box
        display="flex"
        gap={pxToRem(6)}
        padding={pxToRem(6)}
        flexDirection="column"
      >
        {/* Hotel name */}
        <SkeletonText
          rows={1}
          width="80%"
          skeletonHeight={pxToRem(20)}
        />

        {/* Distance */}
        <SkeletonText
          rows={1}
          width="60%"
          skeletonHeight={pxToRem(16)}
        />

        {/* Description */}
        <Box>
          <SkeletonText
            rows={1}
            width="100%"
            paddingBottom={pxToRem(2)}
            skeletonHeight={pxToRem(16)}
          />
          <SkeletonText
            rows={1}
            width="70%"
            skeletonHeight={pxToRem(16)}
          />
        </Box>

        {/* Pricing */}
        <Flex
          justifyContent="space-between"
          alignItems="center"
        >
          <SkeletonText
            rows={1}
            width="40%"
            skeletonHeight={pxToRem(16)}
          />
          <SkeletonText
            rows={1}
            width="30%"
            skeletonHeight={pxToRem(18)}
          />
        </Flex>

        {/* Button - only show for non-grid view */}
        {!isGridView && (
          <SkeletonText
            rows={1}
            width="100%"
            skeletonHeight={pxToRem(44)}
          />
        )}
      </Box>
    </Flex>
  );
};

export const InterestedGuestsLabel = ({ interestedGuestCount }: { interestedGuestCount: number }) => {
  const { t } = useTranslation('guestSiteCustom');
  const accTrans = t('accommodations');
  const [accommodationGuestHotelTileFontUpdatesEnabled] = useIsFeatureEnabled('accommodationGuestHotelTileFontUpdates');
  const [fontPackExperimentEnabled] = useIsFeatureEnabled('fontPackExperiment');
  const { scaleFontSize } = useScaleGuestSiteFontSize();

  return (
    <Flex
      borderRadius={pxToRem(20.632)}
      height={pxToRem(32)}
      alignItems="center"
      paddingX={pxToRem(4)}
      backdropFilter={`blur(${pxToRem(4)})`}
      backgroundColor="rgba(255, 255, 255, 0.6)"
    >
      {accommodationGuestHotelTileFontUpdatesEnabled ? (
        <TextV2
          typographyVariant="label3"
          fontFamily={'Inter UI'}
          color={'black'}
          fontSize={pxToRem(13)}
          letterSpacing={pxToRem(-0.032)}
          fontWeight={600}
          alignSelf={'center'}
        >
          {accTrans.guestsInterested({ interestedGuestCount })}
        </TextV2>
      ) : (
        <GuestSiteTypographyOverride
          override={label3ToParagraphOverride}
          defaultFontFamily="Inter UI"
        >
          <TextV2
            {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
            fontSize={scaleFontSize(pxToRem(13), Category.PARAGRAPH)}
            letterSpacing={pxToRem(-0.032)}
            fontWeight={600}
            alignSelf={'center'}
            color={'black'}
          >
            {accTrans.guestsInterested({ interestedGuestCount })}
          </TextV2>
        </GuestSiteTypographyOverride>
      )}
    </Flex>
  );
};

const VenueDisplayName = ({ displayName }: { displayName: string }) => {
  const [accommodationGuestHotelTileFontUpdatesEnabled] = useIsFeatureEnabled('accommodationGuestHotelTileFontUpdates');
  const [fontPackExperimentEnabled] = useIsFeatureEnabled('fontPackExperiment');
  const { scaleFontSize } = useScaleGuestSiteFontSize();

  if (accommodationGuestHotelTileFontUpdatesEnabled) {
    return (
      <TextEllipsisWrapper
        typographyVariant="hed2"
        fontFamily="Inter UI"
        fontWeight={600}
        color="mono14"
        title={displayName || ''}
      >
        {displayName}
      </TextEllipsisWrapper>
    );
  }

  return (
    <GuestSiteTypographyOverride
      override={hed2ToSubHeadingOverride}
      defaultFontFamily="Inter UI"
    >
      <TextEllipsisWrapper
        typographyVariant="hed2"
        {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
        fontSize={scaleFontSize(pxToRem(17), Category.SUB_HEADING)}
        fontWeight={600}
        color="mono14"
        title={displayName || ''}
      >
        {displayName}
      </TextEllipsisWrapper>
    </GuestSiteTypographyOverride>
  );
};

const Description = ({ description }: { description: string }) => {
  const { t } = useTranslation('guestSiteCustom');
  const accTrans = t('accommodations');
  const { textRef, isOverflowing } = useTextLineClampOverflow(2);
  const [isShowingMoreDescription, setShowMoreDescription] = useState(false);
  const [accommodationGuestHotelTileFontUpdatesEnabled] = useIsFeatureEnabled('accommodationGuestHotelTileFontUpdates');
  const [fontPackExperimentEnabled] = useIsFeatureEnabled('fontPackExperiment');
  const { scaleFontSize } = useScaleGuestSiteFontSize();

  const handleCoupleNoteOpen = useEventCallback(e => {
    e.preventDefault();
    e.stopPropagation();
    setShowMoreDescription(isOpen => !isOpen);
  });
  return (
    <Flex
      width="100%"
      alignItems="flex-end"
    >
      {description && (
        <>
          {accommodationGuestHotelTileFontUpdatesEnabled ? (
            <TextEllipsisWrapper
              ref={textRef}
              lineClamp={isShowingMoreDescription ? 'none' : 2}
              lineHeight={1.5}
              className={ACCOMMODATIONS_NOTE_TEXT_CLASS_NAME}
            >
              <TextV2
                typographyVariant="body1"
                fontFamily="Inter UI"
                fontWeight={400}
                color="mono10"
                title={description}
                lineHeight={1.5}
              >
                {description}
              </TextV2>
            </TextEllipsisWrapper>
          ) : (
            <GuestSiteTypographyOverride
              override={body1ToParagraphOverride}
              defaultFontFamily="Inter UI"
            >
              <TextEllipsisWrapper
                ref={textRef}
                lineClamp={isShowingMoreDescription ? 'none' : 2}
                lineHeight={1.5}
                className={ACCOMMODATIONS_NOTE_TEXT_CLASS_NAME}
              >
                <TextV2
                  typographyVariant="body1"
                  {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
                  fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)}
                  fontWeight={400}
                  color="mono10"
                  title={description}
                  lineHeight={1.5}
                >
                  {description}
                </TextV2>
              </TextEllipsisWrapper>
            </GuestSiteTypographyOverride>
          )}
        </>
      )}
      {isOverflowing &&
        (accommodationGuestHotelTileFontUpdatesEnabled ? (
          <TextV2
            fontWeight={400}
            _hover={{ cursor: 'pointer' }}
            color="mono10"
            onClick={handleCoupleNoteOpen}
            typographyVariant="body1"
            fontFamily="Inter UI"
            lineHeight={1.5}
            textDecoration="underline"
          >
            {isShowingMoreDescription ? `${accTrans.less()}` : `${accTrans.more()}`}
          </TextV2>
        ) : (
          <GuestSiteTypographyOverride
            override={body1ToParagraphOverride}
            defaultFontFamily="Inter UI"
          >
            <TextV2
              {...(!fontPackExperimentEnabled && { fontFamily: 'Inter UI' })}
              fontSize={scaleFontSize(pxToRem(15), Category.PARAGRAPH)}
              fontWeight={400}
              _hover={{ cursor: 'pointer' }}
              color="mono12"
              onClick={handleCoupleNoteOpen}
              typographyVariant="body1"
              lineHeight={1.5}
              textDecoration="underline"
            >
              {isShowingMoreDescription ? `${accTrans.less()}` : `${accTrans.more()}`}
            </TextV2>
          </GuestSiteTypographyOverride>
        ))}
    </Flex>
  );
};

export { AccommodationsTileV2, AccommodationsTileV2Skeleton };
